/**
 * Today's expeditions on the web (decision 0039): what the game keeps of the list in this browser
 * (spec/formats.md, ListSettings), the requests to the Worker's routes under /api/v1/, and the
 * state the result sheet and the menu show. It shows for everyone, unless `?list=0`. Nothing leaves
 * the browser until the player adds an expedition with a name.
 */
import { dayNumber } from "@mistsweeper/core";
import type { ListEntry } from "../worker/store.ts";
import type { DailyResult } from "./runs.ts";
import { getJson, setJson } from "./storage.ts";

export type { ListEntry };

/** What the browser keeps, under `mistsweeper.v1.list`. */
export interface ListSettings {
  readonly v: 1;
  device: string | null;
  name: string | null;
  auto: boolean;
  /** By day in decimal, the id of this browser's expedition on that day's list; the last 30 days. */
  added: Record<string, number>;
  /** Days whose attempt the player asked to add while the list could not be reached. */
  waiting: number[];
}

export interface DaySummary {
  readonly count: number;
  /** Metres, tiles and how many expeditions reached exactly that, in the list's order. */
  readonly ranks: readonly (readonly [number, number, number])[];
}

export interface DayList {
  readonly count: number;
  readonly entries: readonly ListEntry[];
}

/** Why the Worker refused, as the spec names it, or `other` when it could not be reached. */
export type ListError =
  | "name"
  | "refused"
  | "already"
  | "removed"
  | "closed"
  | "busy"
  | "blocked"
  | "notFound"
  | "other";

/** The result sheet's row: the place among today's expeditions and whether this one is on it. */
export interface ListPlace {
  readonly place: number | null;
  readonly count: number | null;
  readonly name: string | null;
  readonly added: boolean;
  readonly waiting: boolean;
}

const KEY = "list";
const KEEP_DAYS = 30;
const TIMEOUT_MS = 10_000;

export function loadListSettings(): ListSettings {
  const saved = getJson<Partial<ListSettings>>(KEY);
  const device =
    typeof saved?.device === "string" && /^[A-Za-z0-9_-]{16,64}$/.test(saved.device)
      ? saved.device
      : null;
  const added: Record<string, number> = {};
  if (saved?.added && typeof saved.added === "object") {
    for (const [day, id] of Object.entries(saved.added)) {
      if (/^\d+$/.test(day) && Number.isSafeInteger(id)) added[day] = id;
    }
  }
  return {
    v: 1,
    device,
    name: typeof saved?.name === "string" ? saved.name : null,
    auto: saved?.auto === true,
    added,
    waiting: Array.isArray(saved?.waiting)
      ? saved.waiting.filter((d): d is number => Number.isSafeInteger(d))
      : [],
  };
}

export function saveListSettings(settings: ListSettings): void {
  setJson(KEY, settings);
}

/** Whether the list shows: for everyone, unless `?list=0` leaves it out for a test or a picture. */
export function listEnabled(search: string): boolean {
  return new URLSearchParams(search).get("list") !== "0";
}

/** A new device's code: 16 random bytes in base64url, 22 characters. */
export function newDevice(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * The place of an expedition of `metres` and `tiles` among the day's: 1 plus those that went
 * further, or as far over more tiles. One not on the list yet counts as one more.
 */
export function placeIn(
  summary: DaySummary,
  metres: number,
  tiles: number,
  onList: boolean,
): { place: number; count: number } {
  let above = 0;
  for (const [m, t, n] of summary.ranks) if (m > metres || (m === metres && t > tiles)) above += n;
  return { place: above + 1, count: summary.count + (onList ? 0 : 1) };
}

/** The Worker's routes, from the same site. */
export class ListClient {
  private readonly base: string;

  constructor(base = "") {
    this.base = base;
  }

  async summary(day: number): Promise<DaySummary | null> {
    const body = await this.get(`/api/v1/days/${day}`);
    return body && Array.isArray(body.ranks)
      ? { count: Number(body.count), ranks: body.ranks as [number, number, number][] }
      : null;
  }

  async top(day: number): Promise<DayList | null> {
    const body = await this.get(`/api/v1/days/${day}/expeditions`);
    return body && Array.isArray(body.entries)
      ? { count: Number(body.count), entries: body.entries as ListEntry[] }
      : null;
  }

  async add(
    device: string,
    name: string,
    result: DailyResult,
    auto = false,
  ): Promise<{ entry: ListEntry; count: number } | ListError> {
    const answer = await this.post(`/api/v1/days/${result.day}/expeditions`, {
      v: 1,
      device,
      name,
      platform: "web",
      record: result.record,
      ...(auto ? { auto } : {}),
    });
    if (typeof answer === "string") return answer;
    return { entry: answer.entry as ListEntry, count: Number(answer.count) };
  }

  async rename(device: string, name: string): Promise<{ name: string } | ListError> {
    const answer = await this.post("/api/v1/devices/name", { v: 1, device, name });
    return typeof answer === "string" ? answer : { name: String(answer.name) };
  }

  async forget(device: string): Promise<true | ListError> {
    const answer = await this.post("/api/v1/devices/forget", { v: 1, device });
    return typeof answer === "string" ? answer : true;
  }

  async report(device: string, expedition: number): Promise<true | ListError> {
    const answer = await this.post("/api/v1/reports", { v: 1, device, expedition });
    return typeof answer === "string" ? answer : true;
  }

  private async get(path: string): Promise<Record<string, unknown> | null> {
    try {
      const response = await fetch(this.base + path, {
        cache: "no-store",
        signal: AbortSignal.timeout(TIMEOUT_MS),
      });
      return response.ok ? ((await response.json()) as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }

  private async post(path: string, body: unknown): Promise<Record<string, unknown> | ListError> {
    try {
      const response = await fetch(this.base + path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(TIMEOUT_MS),
      });
      const answer = (await response.json().catch(() => null)) as Record<string, unknown> | null;
      if (response.ok && answer) return answer;
      const code = answer?.error;
      const known: readonly ListError[] = [
        "name",
        "refused",
        "already",
        "removed",
        "closed",
        "busy",
        "blocked",
        "notFound",
      ];
      return known.includes(code as ListError) ? (code as ListError) : "other";
    } catch {
      return "other";
    }
  }
}

/**
 * The list as the game uses it: this browser's settings, the last summary and list fetched for
 * each day, and adding the day's attempt, by hand or by itself. `changed` is called whenever what
 * the result sheet or the menu shows has changed.
 */
export class TodaysList {
  readonly enabled: boolean;
  settings: ListSettings;
  private readonly client: ListClient;
  private readonly changed: () => void;
  private readonly summaries = new Map<number, DaySummary>();
  private readonly lists = new Map<number, DayList | "unreachable">();
  /** The place and count the Worker answered when this browser's expedition was added, by day. */
  private readonly answers = new Map<number, { place: number; count: number }>();
  /** Days being sent now, so a day is never sent twice at once. */
  private readonly sending = new Set<number>();

  constructor(options: {
    enabled: boolean;
    changed: () => void;
    client?: ListClient;
    settings?: ListSettings;
  }) {
    this.enabled = options.enabled;
    this.changed = options.changed;
    this.client = options.client ?? new ListClient();
    this.settings = options.settings ?? loadListSettings();
  }

  /** The id of this browser's expedition on the day's list, or null. */
  addedOn(day: number): number | null {
    return this.settings.added[String(day)] ?? null;
  }

  /**
   * The result sheet's row for a day's result: its place from the day's summary, or from the
   * Worker's answer when it was added, whichever counted more expeditions, the fresher.
   */
  place(result: DailyResult): ListPlace {
    const summary = this.summaries.get(result.day);
    const added = this.addedOn(result.day) !== null;
    let at = summary ? placeIn(summary, result.metres, result.tiles, added) : null;
    const answer = added ? this.answers.get(result.day) : undefined;
    if (answer && (!at || at.count < answer.count)) at = answer;
    return {
      place: at?.place ?? null,
      count: at?.count ?? null,
      name: this.settings.name,
      added,
      waiting: this.settings.waiting.includes(result.day),
    };
  }

  /** Fetches a day's summary for the place on the result sheet. */
  async refresh(day: number): Promise<void> {
    const summary = await this.client.summary(day);
    if (summary) this.summaries.set(day, summary);
    this.changed();
  }

  /** Fetches a day's list for the menu's page, and its summary for the player's own place. */
  async load(day: number): Promise<void> {
    const [list, summary] = await Promise.all([this.client.top(day), this.client.summary(day)]);
    this.lists.set(day, list ?? "unreachable");
    if (summary) this.summaries.set(day, summary);
    this.changed();
  }

  /** The day's list as last fetched, "unreachable", or null before it has been. */
  list(day: number): DayList | "unreachable" | null {
    return this.lists.get(day) ?? null;
  }

  summary(day: number): DaySummary | null {
    return this.summaries.get(day) ?? null;
  }

  /**
   * Adds a day's result under `name`, or the name kept. The first one makes the device's code, if
   * a report has not, and turns adding by itself on. `auto` says the game sent it by itself, which
   * the Worker refuses just after a removal (spec/formats.md, After a removal). Gives back why it
   * failed, or null.
   */
  async add(
    result: DailyResult,
    name = this.settings.name,
    auto = false,
  ): Promise<ListError | null> {
    if (!name || this.sending.has(result.day)) return null;
    this.sending.add(result.day);
    // The first expedition added, the name not chosen before, turns adding by itself on.
    const first = this.settings.name === null;
    try {
      const device = this.settings.device ?? newDevice();
      const answer = await this.client.add(device, name, result, auto);
      const waiting = this.settings.waiting.filter((d) => d !== result.day);
      if (answer === "other") {
        // It waits only once a name is kept, so it can go by itself; the first is tried again.
        if (!first) this.settings.waiting = [...waiting, result.day];
        this.save();
        return answer;
      }
      this.settings.waiting = waiting;
      if (typeof answer === "string") {
        this.save();
        if (answer === "removed") void this.refresh(result.day);
        return answer;
      }
      this.settings.device = device;
      this.settings.name = answer.entry.name ?? name;
      if (first) this.settings.auto = true;
      this.settings.added = recent({ ...this.settings.added, [result.day]: answer.entry.id });
      const shown = this.lists.has(result.day);
      this.answers.set(result.day, { place: answer.entry.place, count: answer.count });
      this.lists.delete(result.day);
      this.save();
      // The page showing the day's list fetches it again, with this expedition on it.
      if (shown) void this.load(result.day);
      return null;
    } finally {
      this.sending.delete(result.day);
      this.changed();
    }
  }

  /**
   * Sends what should be on a list and is not: each of today's and yesterday's results, while
   * their lists are open, when adding by itself is on or the player asked while offline. `byHand`
   * when the player has just asked for it, by turning adding by itself on.
   */
  async catchUp(
    now: number,
    resultOf: (day: number) => DailyResult | null,
    byHand = false,
  ): Promise<void> {
    if (!this.enabled || !this.settings.device || !this.settings.name) return;
    const today = dayNumber(now);
    for (const day of [today - 1, today]) {
      if (this.addedOn(day) !== null) continue;
      if (!this.settings.auto && !this.settings.waiting.includes(day)) continue;
      const result = resultOf(day);
      if (result) await this.add(result, this.settings.name, !byHand);
    }
  }

  setAuto(on: boolean): void {
    this.settings.auto = on;
    this.save();
    this.changed();
  }

  /** A new name on every expedition this browser has added. */
  async rename(name: string): Promise<ListError | null> {
    if (!this.settings.device) {
      this.settings.name = name;
      this.save();
      this.changed();
      return null;
    }
    const answer = await this.client.rename(this.settings.device, name);
    if (typeof answer === "string") return answer;
    this.settings.name = answer.name;
    this.lists.clear();
    this.save();
    this.changed();
    return null;
  }

  /** Takes this browser's expeditions off every list and stops adding them. */
  async forget(): Promise<ListError | null> {
    if (!this.settings.device) return null;
    const answer = await this.client.forget(this.settings.device);
    if (answer !== true) return answer;
    this.settings.added = {};
    this.settings.waiting = [];
    this.settings.auto = false;
    this.lists.clear();
    this.summaries.clear();
    this.save();
    this.changed();
    return null;
  }

  async report(expedition: number): Promise<ListError | null> {
    const device = this.settings.device ?? newDevice();
    if (!this.settings.device) {
      // A report needs a device's code to count once; it is kept, though nothing was added.
      this.settings.device = device;
      this.save();
    }
    const answer = await this.client.report(device, expedition);
    return answer === true ? null : answer;
  }

  private save(): void {
    saveListSettings(this.settings);
  }
}

/** Keeps the last KEEP_DAYS days of `added`. */
function recent(added: Record<string, number>): Record<string, number> {
  const days = Object.keys(added)
    .map(Number)
    .sort((a, b) => b - a)
    .slice(0, KEEP_DAYS);
  return Object.fromEntries(days.map((d) => [String(d), added[String(d)] as number]));
}
